import React, { useMemo, useState } from 'react';
import type { PublishedSnapshot } from '../types/snapshot';
import { getPublicApiUrl } from '../services/api';
import { detectViewerLocale, viewerLocaleOptions, viewerT } from '../i18n/viewerI18n';

const DEFAULT_BRAND = '#2563eb';

const formatValue = (value: unknown): string => {
  if (value == null) return '—';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

interface Props {
  snapshot: PublishedSnapshot;
  onLocaleChange?: (locale: string) => void;
  activeLocale?: string;
}

const SnapshotDPPViewer: React.FC<Props> = ({ snapshot, onLocaleChange, activeLocale }) => {
  const [locale, setLocale] = useState(activeLocale || snapshot.locale || detectViewerLocale());
  const [linkCopied, setLinkCopied] = useState(false);
  const brandColor = snapshot.branding?.primary_color || DEFAULT_BRAND;
  const brandName = snapshot.branding?.organisation_name || 'Organisation';
  const availableLocales = snapshot.availableLocales?.length
    ? snapshot.availableLocales
    : viewerLocaleOptions().map((l) => l.code);

  const allFields = useMemo(
    () => (snapshot.sections || []).flatMap((s) => s.fields || []),
    [snapshot.sections]
  );

  const productName =
    (snapshot.product?.name as string) ||
    allFields.find((f) => f.name === 'product_name')?.value?.toString() ||
    snapshot.passportNumber;

  const localeOptions = useMemo(
    () => viewerLocaleOptions(availableLocales),
    [availableLocales]
  );

  const t = (key: Parameters<typeof viewerT>[0]) => viewerT(key, locale);

  const handleLocaleChange = (next: string) => {
    setLocale(next);
    onLocaleChange?.(next);
  };

  const pageUrl = typeof window !== 'undefined' ? window.location.href : '';
  const qrImageUrl = pageUrl
    ? `https://api.qrserver.com/v1/create-qr-code/?size=140x140&data=${encodeURIComponent(pageUrl)}`
    : null;
  const jsonLdUrl = snapshot.uniqueIdentifier
    ? getPublicApiUrl(`/v1/passports/${encodeURIComponent(snapshot.uniqueIdentifier)}/jsonld`)
    : null;
  const pdfUrl = snapshot.uniqueIdentifier
    ? getPublicApiUrl(`/passports/${encodeURIComponent(snapshot.uniqueIdentifier)}/pdf`)
    : null;
  const hasDop = (snapshot.evidence || []).some((ev) =>
    /declaration|conformity|dop/i.test(`${ev.name} ${ev.type}`)
  );
  const dopUrl =
    hasDop && snapshot.uniqueIdentifier
      ? getPublicApiUrl(`/passports/${encodeURIComponent(snapshot.uniqueIdentifier)}/dop`)
      : null;

  const copyLink = async () => {
    if (!pageUrl) return;
    try {
      await navigator.clipboard.writeText(pageUrl);
      setLinkCopied(true);
      window.setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      /* clipboard unavailable */
    }
  };

  const populatedCount = allFields.filter((f) => f.value != null && f.value !== '').length;
  const publishedDate = snapshot.publishedAt
    ? new Date(snapshot.publishedAt).toLocaleDateString(locale)
    : null;

  const stats: Array<{ label: string; value: string }> = [
    { label: t('published'), value: publishedDate || '—' },
    { label: 'Version', value: `v${snapshot.versionNumber ?? 1}` },
    { label: t('productDetails'), value: `${populatedCount} / ${allFields.length}` },
    ...(snapshot.identifiers?.passportType
      ? [{ label: t('type'), value: String(snapshot.identifiers.passportType) }]
      : []),
  ];

  return (
    <>
      <a href="#dpp-main" className="skip-link">
        {t('skipToContent')}
      </a>
      <div className="container">
        <main id="dpp-main" role="main" aria-labelledby="dpp-product-title">
          <div className="card" style={{ borderTop: `4px solid ${brandColor}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
              <div>
                {snapshot.branding?.logo_url ? (
                  <img
                    src={snapshot.branding.logo_url}
                    alt={`${brandName} ${t('organisationLogo')}`}
                    style={{ maxHeight: 40, marginBottom: 8 }}
                  />
                ) : null}
                <p style={{ color: brandColor, fontWeight: 600, fontSize: 12, margin: 0 }}>{t('digitalProductPassport')}</p>
                <h1 id="dpp-product-title" className="card-title" style={{ marginTop: 8 }}>{productName}</h1>
                <p className="text-muted" style={{ fontFamily: 'monospace', fontSize: 13 }}>
                  {snapshot.passportNumber} · v{snapshot.versionNumber ?? 1}
                </p>
              </div>
              {localeOptions.length > 1 && (
                <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
                  {t('language')}
                  <select
                    value={locale}
                    onChange={(e) => handleLocaleChange(e.target.value)}
                    aria-label={t('selectLanguage')}
                    className="locale-select"
                  >
                    {localeOptions.map((opt) => (
                      <option key={opt.code} value={opt.code}>{opt.label}</option>
                    ))}
                  </select>
                </label>
              )}
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                gap: 12,
                marginTop: 16,
                paddingTop: 16,
                borderTop: '1px solid #e5e7eb',
              }}
            >
              {stats.map((stat) => (
                <div key={stat.label}>
                  <div className="text-muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    {stat.label}
                  </div>
                  <div style={{ fontSize: 16, fontWeight: 700 }}>{stat.value}</div>
                </div>
              ))}
            </div>
          </div>

          {snapshot.identifiers && (
            <section className="card" aria-labelledby="identifiers-heading">
              <h2 id="identifiers-heading" className="section-title">{t('productIdentifiers')}</h2>
              <div className="grid grid-2">
                {snapshot.identifiers.product && (
                  <div className="grid-item">
                    <div className="grid-label">{t('productId')}</div>
                    <div className="grid-value">{snapshot.identifiers.product}</div>
                  </div>
                )}
                {snapshot.identifiers.operator && (
                  <div className="grid-item">
                    <div className="grid-label">{t('operatorId')}</div>
                    <div className="grid-value">{snapshot.identifiers.operator}</div>
                  </div>
                )}
                {snapshot.identifiers.facility && (
                  <div className="grid-item">
                    <div className="grid-label">{t('facilityId')}</div>
                    <div className="grid-value">{snapshot.identifiers.facility}</div>
                  </div>
                )}
                {snapshot.identifiers.registration && (
                  <div className="grid-item">
                    <div className="grid-label">{t('dppRegistrationId')}</div>
                    <div className="grid-value">{snapshot.identifiers.registration}</div>
                  </div>
                )}
              </div>
            </section>
          )}

          {(snapshot.sections || []).map((section) => (
            <section className="card" key={section.name} aria-labelledby={`section-${section.name}`}>
              <h2 id={`section-${section.name}`} className="section-title">{section.name}</h2>
              <div className="grid grid-2">
                {(section.fields || []).map((field) => (
                  <div className="grid-item" key={`${section.name}-${field.name}`}>
                    <div className="grid-label">
                      {field.label}
                      {field.isRegulatoryMandatory ? (
                        <span className="field-flag field-flag-mandatory" title={t('mandatory')}>
                          {' '}{t('mandatory')}
                        </span>
                      ) : null}
                      {field.isVoluntary ? (
                        <span className="field-flag field-flag-voluntary" title={t('voluntary')}>
                          {' '}{t('voluntary')}
                        </span>
                      ) : null}
                    </div>
                    <div className="grid-value">{formatValue(field.value)}</div>
                  </div>
                ))}
              </div>
            </section>
          ))}

          {(snapshot.evidence || []).length > 0 && (
            <section className="card" aria-labelledby="documents-heading">
              <h2 id="documents-heading" className="section-title">{t('documents')}</h2>
              <ul>
                {snapshot.evidence?.map((ev) => (
                  <li key={ev.name}>
                    {ev.url ? (
                      <a href={ev.url} target="_blank" rel="noopener noreferrer">
                        {ev.name}
                      </a>
                    ) : (
                      ev.name
                    )}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {(snapshot.lifecycle || []).length > 0 && (
            <section className="card" aria-labelledby="lifecycle-heading">
              <h2 id="lifecycle-heading" className="section-title">Product lifecycle</h2>
              <ol style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {snapshot.lifecycle!.map((event, i) => (
                  <li
                    key={`${event.type}-${event.at}-${i}`}
                    style={{
                      display: 'flex',
                      gap: 12,
                      padding: '8px 0',
                      borderBottom: i < snapshot.lifecycle!.length - 1 ? '1px solid #f3f4f6' : 'none',
                    }}
                  >
                    <span
                      aria-hidden
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        marginTop: 5,
                        flexShrink: 0,
                        background: event.type === 'published' ? '#16a34a' : brandColor,
                      }}
                    />
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>
                        {event.type.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase())}
                      </div>
                      <div className="text-muted" style={{ fontSize: 12 }}>
                        {new Date(event.at).toLocaleDateString(locale)}
                        {event.description ? ` — ${event.description}` : ''}
                      </div>
                    </div>
                  </li>
                ))}
              </ol>
            </section>
          )}

          <section className="card" aria-label="Share and machine-readable formats">
            <div style={{ display: 'flex', gap: 20, alignItems: 'center', flexWrap: 'wrap' }}>
              {qrImageUrl && (
                <img
                  src={qrImageUrl}
                  alt="QR code for this passport"
                  width={140}
                  height={140}
                  style={{ borderRadius: 8, border: '1px solid #e5e7eb' }}
                />
              )}
              <div style={{ flex: 1, minWidth: 200 }}>
                <h2 className="section-title" style={{ marginBottom: 8 }}>Scan or share</h2>
                <p className="text-muted" style={{ margin: '0 0 12px' }}>
                  This passport is permanently available at this address, including machine-readable formats.
                </p>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={copyLink}
                    style={{
                      padding: '8px 14px',
                      borderRadius: 8,
                      border: '1px solid #d1d5db',
                      background: 'white',
                      cursor: 'pointer',
                      fontSize: 13,
                    }}
                  >
                    {linkCopied ? 'Link copied' : 'Copy link'}
                  </button>
                  {pdfUrl && (
                    <a
                      href={pdfUrl}
                      style={{
                        padding: '8px 14px',
                        borderRadius: 8,
                        border: '1px solid #d1d5db',
                        background: 'white',
                        fontSize: 13,
                        textDecoration: 'none',
                        color: 'inherit',
                        display: 'inline-block',
                      }}
                    >
                      {t('download')} PDF
                    </a>
                  )}
                  {dopUrl && (
                    <a
                      href={dopUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        padding: '8px 14px',
                        borderRadius: 8,
                        border: '1px solid #d1d5db',
                        background: 'white',
                        fontSize: 13,
                        textDecoration: 'none',
                        color: 'inherit',
                        display: 'inline-block',
                      }}
                    >
                      Declaration of Conformity
                    </a>
                  )}
                  {jsonLdUrl && (
                    <a
                      href={jsonLdUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        padding: '8px 14px',
                        borderRadius: 8,
                        border: '1px solid #d1d5db',
                        background: 'white',
                        fontSize: 13,
                        textDecoration: 'none',
                        color: 'inherit',
                        display: 'inline-block',
                      }}
                    >
                      JSON-LD
                    </a>
                  )}
                </div>
              </div>
            </div>
          </section>

          <footer className="card text-center mt-3">
            <p className="text-muted" style={{ fontSize: 12, margin: 0 }}>
              {brandName} · {t('poweredBy')}
            </p>
            <p className="text-muted" style={{ fontSize: 11, margin: '6px 0 0' }}>
              {snapshot.publishedAt ? (
                <>
                  {t('lastUpdated')} {new Date(snapshot.publishedAt).toLocaleString(locale)} · v
                  {snapshot.versionNumber ?? 1}
                  {' · '}
                </>
              ) : null}
              {t('uniqueId')}: {snapshot.uniqueIdentifier}
            </p>
          </footer>
        </main>
      </div>
    </>
  );
};

export default SnapshotDPPViewer;
